import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import useChatDispatchActions from '../../../customHooks/useChatDispatchActions';

import {
    ADD_INTERNAL_CHAT_GROUP_MESSAGES,
    APPLY_INTERNAL_CHAT_GROUP_UPDATE,
    PATCH_INTERNAL_CHAT_GROUP
} from '../../../helpers/internalChatConstants';
import { messageGroupsService } from '../../../newServices/corporateChat';
import {
    GROUP_EXIT_REASONS,
    getApiErrorMessage,
    getGroupExitReason
} from '../../../utils/internalChatGroups';

import internalChatToast from './internalChatToast';

const MESSAGES_PER_PAGE = 20;

const requestMessagesPage = (groupId, lastId) => {
    return messageGroupsService
        .getMessageGroupMessages(groupId, { lastId, orderBy: 'DESC', perPage: MESSAGES_PER_PAGE })
        .then(res => (res && res.data) || []);
}

/** O que acontece quando a janela do grupo abre:
 *  - as não lidas vão a 0 (só no front: a API não tem rota de leitura de grupo);
 *  - o grupo completo é buscado toda vez, e não só na primeira: é assim que chegam os
 *    participantes (a listagem não traz) e as mudanças feitas por outra pessoa;
 *  - o histórico só é buscado se ainda não veio. As mensagens da criação não contam.
 *  Enquanto as buscas não voltam, a janela funciona com o que já tem.
 */
function useGroupWindow(groupId) {
    const intl = useIntl();
    const dispatchChatActions = useChatDispatchActions();

    const loggedUserId = useSelector(state => state.auth.userData?.id);
    const group = useSelector(state => state.internalChat.groups.find(item => item.id === groupId)) || null;

    /** Guarda o id do grupo em carregamento: trocar de grupo no meio da busca não pode
     *  deixar o indicador preso no grupo novo.
     */
    const [loadingGroupId, setLoadingGroupId] = useState(null);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

    const groupRef = useRef(group);
    groupRef.current = group;
    const loadingOlderRef = useRef(false);

    useEffect(() => {
        if (!groupId) return;

        const openedGroupName = groupRef.current?.name;

        dispatchChatActions(PATCH_INTERNAL_CHAT_GROUP, { id: groupId, changes: { totalUnread: 0 } });

        messageGroupsService.getMessageGroup(groupId)
            .then(res => {
                const data = res && res.data;
                if (!data) return;

                /** Sem o aviso, o grupo que saiu da lista sumiria logo depois do clique. */
                const exitReason = getGroupExitReason(data, loggedUserId);
                const name = data.name || openedGroupName;
                if (exitReason === GROUP_EXIT_REASONS.DELETED) {
                    internalChatToast.info(intl.formatMessage({ id: 'O grupo "{name}" foi excluído.' }, { name }));
                } else if (exitReason === GROUP_EXIT_REASONS.REMOVED) {
                    internalChatToast.info(intl.formatMessage({ id: 'Você não faz mais parte do grupo "{name}".' }, { name }));
                }

                dispatchChatActions(APPLY_INTERNAL_CHAT_GROUP_UPDATE, { group: data, loggedUserId });
            })
            .catch(error => {
                internalChatToast.error(
                    getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível atualizar os dados do grupo." })
                );
            });

        if (groupRef.current?.messagesLoaded) return;

        setLoadingGroupId(groupId);
        requestMessagesPage(groupId, 0)
            .then(messages => {
                dispatchChatActions(ADD_INTERNAL_CHAT_GROUP_MESSAGES, {
                    id: groupId,
                    messages,
                    changes: { messagesLoaded: true, noMoreMessages: messages.length === 0 }
                });
            })
            .catch(error => {
                internalChatToast.error(
                    getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível carregar as mensagens do grupo." })
                );
            })
            .finally(() => {
                setLoadingGroupId(prevState => prevState === groupId ? null : prevState);
            });
    }, [groupId]);

    /** O cursor é a mensagem mais antiga já carregada. Página vazia = acabou o histórico. */
    const loadOlderMessages = useCallback(() => {
        const currentGroup = groupRef.current;
        const oldestMessage = currentGroup?.messages?.[0];

        if (!currentGroup || !currentGroup.messagesLoaded || currentGroup.noMoreMessages || !oldestMessage || loadingOlderRef.current) return;

        loadingOlderRef.current = true;
        setLoadingOlderMessages(true);

        requestMessagesPage(currentGroup.id, oldestMessage.id)
            .then(messages => {
                dispatchChatActions(ADD_INTERNAL_CHAT_GROUP_MESSAGES, {
                    id: currentGroup.id,
                    messages,
                    changes: { noMoreMessages: messages.length === 0 }
                });
            })
            .catch(error => {
                internalChatToast.error(
                    getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível carregar as mensagens do grupo." })
                );
            })
            .finally(() => {
                loadingOlderRef.current = false;
                setLoadingOlderMessages(false);
            });
    }, [dispatchChatActions, intl]);

    return {
        loadingMessages: !!groupId && loadingGroupId === groupId,
        loadingOlderMessages,
        loadOlderMessages
    };
}

export default useGroupWindow;
